import { useCallback, useEffect, useRef, useState, TouchEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import { mediaApi }   from '@/api/media';
import { Spinner }    from '@/components/ui';
import {
  Thermometer, ArrowLeft, Maximize, RefreshCw, IceCream,
  ChevronLeft, ChevronRight, Minus, Plus,
} from 'lucide-react';
import { splitTextSteps } from '@/lib/utils';
import { getRecipeClassification } from '@/lib/recipeTaxonomy';

// Kitchen detail page — optimised for tablets mounted in the kitchen.
// Large typography, high-contrast, fullscreen support, auto-refresh.
export default function KitchenDetailPage() {
  const { t }      = useTranslation();
  const { id }     = useParams<{ id: string }>();
  const navigate   = useNavigate();
  const REFRESH_MS = 60_000;

  const { data: recipe, isLoading, dataUpdatedAt, refetch } = useQuery({
    queryKey:        ['recipe', id],
    queryFn:         () => recipesApi.get(id!),
    enabled:         !!id,
    refetchInterval: REFRESH_MS,
  });

  // Recipe list for prev / next navigation
  const { data: listResult } = useQuery({
    queryKey: ['recipes', { limit: 200 }],
    queryFn:  () => recipesApi.list({ page: 1, limit: 200 }),
  });

  // Media
  const { data: mediaItems = [] } = useQuery({
    queryKey: ['recipe-media', id],
    queryFn:  () => mediaApi.list(id!),
    enabled:  !!id,
  });

  // Local integer multiplier — reset whenever the recipe changes
  const [localMultiplier, setLocalMultiplier] = useState(1);
  const prevIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (recipe && recipe.id !== prevIdRef.current) {
      prevIdRef.current = recipe.id;
      setLocalMultiplier(Math.max(1, Math.round(recipe.multiplier)));
    }
  }, [recipe]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {/* ignore */});
    } else {
      document.exitFullscreen().catch(() => {/* ignore */});
    }
  }, []);

  // Prev / next recipe
  const recipeList  = listResult?.data ?? [];
  const currentIdx  = recipeList.findIndex(r => r.id === id);
  const prevId      = currentIdx > 0 ? recipeList[currentIdx - 1]?.id : null;
  const nextId      = currentIdx >= 0 && currentIdx < recipeList.length - 1
    ? recipeList[currentIdx + 1]?.id : null;

  // Touch swipe — horizontal swipe navigates prev/next
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 60) return;          // ignore small movements
    if (dx < 0 && nextId) navigate(`/kitchen/${nextId}`);
    if (dx > 0 && prevId) navigate(`/kitchen/${prevId}`);
  };

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner size="lg" className="h-12 w-12" />
    </div>
  );

  if (!recipe) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <IceCream size={40} className="text-muted-foreground" />
      <p className="text-xl text-muted-foreground">Recipe not found.</p>
      <Link to="/kitchen" className="text-primary underline">{t('common.back')}</Link>
    </div>
  );

  const calc           = recipe.calculations;
  const classification = getRecipeClassification(recipe, t);
  const baseMultiplier = recipe.multiplier || 1;

  // Scale production values by the local multiplier
  const scaledProduction = calc
    ? (calc.total_production_grams / baseMultiplier) * localMultiplier
    : 0;
  const scaledPortions = calc
    ? Math.round((calc.number_of_portions / baseMultiplier) * localMultiplier)
    : 0;

  return (
    <div
      className="kitchen-display min-h-screen bg-background pb-16"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-card px-4 py-3 sm:px-6 print:hidden">

        {/* Left: back */}
        <Link
          to="/kitchen"
          className="flex items-center gap-1.5 rounded px-2 py-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm hidden sm:inline">{t('common.back')}</span>
        </Link>

        {/* Centre: recipe name + position */}
        <div className="flex flex-col items-center min-w-0">
          <p className="text-base font-semibold truncate max-w-xs text-foreground">{recipe.name}</p>
          {currentIdx >= 0 && (
            <p className="text-xs text-muted-foreground">{currentIdx + 1} / {recipeList.length}</p>
          )}
        </div>

        {/* Right: refresh + fullscreen */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title={`${t('kitchen.lastRefresh')}: ${new Date(dataUpdatedAt).toLocaleTimeString()}`}
          >
            <RefreshCw size={15} />
            <span className="hidden sm:inline">{t('kitchen.refresh')}</span>
          </button>
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <Maximize size={15} />
            <span className="hidden sm:inline">{t('kitchen.fullscreen')}</span>
          </button>
        </div>
      </div>

      {/* ── Fixed side nav buttons ──────────────────────────────────────── */}
      {prevId && (
        <button
          onClick={() => navigate(`/kitchen/${prevId}`)}
          className="fixed left-0 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center w-12 h-28 rounded-r-2xl bg-card/80 border border-l-0 backdrop-blur shadow-lg text-muted-foreground hover:text-foreground hover:bg-card active:scale-95 transition-all print:hidden"
          aria-label="Previous recipe"
        >
          <ChevronLeft size={28} />
        </button>
      )}
      {nextId && (
        <button
          onClick={() => navigate(`/kitchen/${nextId}`)}
          className="fixed right-0 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center w-12 h-28 rounded-l-2xl bg-card/80 border border-r-0 backdrop-blur shadow-lg text-muted-foreground hover:text-foreground hover:bg-card active:scale-95 transition-all print:hidden"
          aria-label="Next recipe"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 space-y-8">

        {/* Recipe heading */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight">{recipe.name}</h1>
          {classification.length > 0 && (
            <p className="mt-2 text-xl text-muted-foreground">{classification.join(' · ')}</p>
          )}
        </div>

        {/* Technical balance — at top */}
        {calc && (
          <div>
            <h2 className="text-2xl font-bold mb-3">{t('recipes.technicals')}</h2>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-7">
              <MiniStat label="Sugars" value={`${calc.total_sugars_pct.toFixed(1)}%`} />
              <MiniStat label="Fat"    value={`${calc.total_fat_pct.toFixed(1)}%`} />
              <MiniStat label="SML"    value={`${calc.total_sml_pct.toFixed(1)}%`} />
              <MiniStat label="SNFM"   value={`${calc.total_solids_diff.toFixed(1)}%`} />
              <MiniStat label="Solids" value={`${calc.total_solids_total.toFixed(1)}%`} />
              <MiniStat label="POD"    value={calc.total_pod.toFixed(0)} />
              <MiniStat label="PAC"    value={calc.total_pac.toFixed(0)} highlight />
            </div>
          </div>
        )}

        {/* Allergens */}
        {recipe.allergens && recipe.allergens.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-3">{t('recipes.allergens')}</h2>
            <div className="flex flex-wrap gap-2">
              {recipe.allergens.map(a => (
                <span
                  key={a.id}
                  className="rounded-full border border-amber-300 bg-amber-100 px-4 py-1.5 text-base font-semibold text-amber-800 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
                >
                  {a.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Serving temperature — hero element */}
        {calc && (
          <div className={`flex items-center gap-4 rounded-2xl p-5 ${
            calc.suggested_serving_temp === 'PAC ERROR'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
          }`}>
            <Thermometer size={36} />
            <div>
              <p className="text-lg font-medium text-muted-foreground">{t('recipes.servingTemp')}</p>
              <p className="text-5xl font-black tracking-tight">{calc.suggested_serving_temp}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-sm text-muted-foreground">PAC</p>
              <p className="text-3xl font-bold">{calc.total_pac}</p>
            </div>
          </div>
        )}

        {/* Production summary + active multiplier control */}
        {calc && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatBox label={t('recipes.totalProduction')} value={`${scaledProduction.toFixed(0)} g`} />
            <StatBox label={t('recipes.portions')} value={String(scaledPortions)} />

            {/* Multiplier stepper */}
            <div className="col-span-2 sm:col-span-1 rounded-xl border bg-card p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">{t('recipes.multiplier')}</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setLocalMultiplier(m => Math.max(1, m - 1))}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border bg-muted/40 hover:bg-accent transition-colors"
                >
                  <Minus size={18} />
                </button>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={localMultiplier}
                  onChange={e => {
                    const v = Math.round(Number(e.target.value));
                    if (v >= 1) setLocalMultiplier(v);
                  }}
                  className="w-16 bg-transparent text-center text-3xl font-bold outline-none"
                />
                <button
                  onClick={() => setLocalMultiplier(m => m + 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border bg-muted/40 hover:bg-accent transition-colors"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Media gallery */}
        {mediaItems.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-4">{t('media.tabLabel')}</h2>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {mediaItems.map(m => (
                m.media_type === 'photo' ? (
                  <img
                    key={m.id}
                    src={m.thumb_url ?? m.url}
                    alt={m.original_name}
                    className="h-52 w-auto shrink-0 cursor-pointer rounded-xl object-cover"
                    onClick={() => window.open(m.url, '_blank')}
                  />
                ) : (
                  <video
                    key={m.id}
                    src={m.url}
                    controls
                    className="h-52 w-auto shrink-0 rounded-xl"
                  />
                )
              ))}
            </div>
          </div>
        )}

        {/* Ingredient lines */}
        <div>
          <h2 className="text-3xl font-bold mb-4">{t('recipes.ingredients')}</h2>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-lg font-semibold">{t('ingredients.name')}</th>
                  <th className="text-right p-4 text-lg font-semibold">{t('recipes.gramsPerKilo')}</th>
                  <th className="text-right p-4 text-lg font-semibold hidden sm:table-cell">{t('recipes.totalGrams')}</th>
                </tr>
              </thead>
              <tbody>
                {recipe.ingredients.map((line, idx) => (
                  <tr
                    key={line.id ?? idx}
                    className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  >
                    <td className="p-4 text-xl font-medium">{line.ingredient_name}</td>
                    <td className="p-4 text-xl text-right font-mono font-semibold">
                      {Number(line.grams_per_kilo).toFixed(1)} g
                    </td>
                    <td className="p-4 text-lg text-right text-muted-foreground font-mono hidden sm:table-cell">
                      {(Number(line.grams_per_kilo) * localMultiplier).toFixed(1)} g
                      {Number(line.waste_ratio) > 0 && (
                        <span className="text-sm ml-1">(+{(Number(line.waste_ratio) * 100).toFixed(0)}% waste)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {calc && (
                <tfoot className="border-t-2 font-bold bg-card">
                  <tr>
                    <td className="p-4 text-xl">{t('common.total')}</td>
                    <td className="p-4 text-xl text-right font-mono">
                      {recipe.ingredients.reduce((s, l) => s + Number(l.grams_per_kilo), 0).toFixed(1)} g
                    </td>
                    <td className="p-4 hidden sm:table-cell" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Kitchen notes */}
        {recipe.kitchen_notes && (
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('recipes.kitchenNotes')}</h2>
            <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 p-5">
              <p className="text-xl whitespace-pre-wrap leading-relaxed">{recipe.kitchen_notes}</p>
            </div>
          </div>
        )}

        {recipe.preparation_notes && (
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('recipes.preparationNotes')}</h2>
            <OrderedSteps text={recipe.preparation_notes} />
          </div>
        )}

        {recipe.presentation_notes && (
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('recipes.presentationNotes')}</h2>
            <p className="text-xl whitespace-pre-wrap leading-relaxed">{recipe.presentation_notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderedSteps({ text }: { text: string }) {
  const steps = splitTextSteps(text);

  if (steps.length === 0) {
    return <p className="text-xl whitespace-pre-wrap leading-relaxed">{text}</p>;
  }

  return (
    <ol className="list-decimal space-y-3 pl-8 text-xl leading-relaxed marker:font-bold">
      {steps.map((step, index) => (
        <li key={`${index}-${step}`}>{step}</li>
      ))}
    </ol>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 text-center">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-2 text-center ${highlight ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/30' : 'bg-muted/30'}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${highlight ? 'text-blue-700 dark:text-blue-300' : ''}`}>{value}</p>
    </div>
  );
}
